import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAudit } from "./AuditContext";
import { PresenterFloorShadow } from "./PresenterFloorShadow";
import { buildPatternRead } from "./patternEngine";
import { buildAuditAnalysisInput } from "./auditNormalization";
import { ROUTES } from "../../config/links";
import { getGaClientId, getAttributionContext, identify, capture, captureOaiqEvent, pushDataLayerEvent, trackCta } from "../../lib/analytics";
import { exitRoom, getAuditTotals, claimAuditCompletedEvent } from "./auditAnalyticsTiming";
import { useIsMobileAudit } from "./useIsMobileAudit";

// Polling interval for GET /api/audit/complete/{pipelineRunId} — reasonable,
// not aggressive (the full Operations 2-15 pipeline takes real time).
const PIPELINE_POLL_INTERVAL_MS = 4000;

/**
 * Results Room — final Audit results flow.
 * Presenter (thinking) on the RIGHT, progressive feedback + report form on
 * the LEFT. Same rehearsal-room language as every other room.
 *
 * Phases: intro bubbles → 3 sections revealed one at a time → outro bubbles
 * → report form → submitted bubbles → booking CTA.
 */

const EASE = [0.16, 1, 0.3, 1];
const BASE_PRESENTER_HEIGHT = 104;
const BOOKING_LINK = ROUTES.bookPresentation; // presentation-specific context (High-Stakes Audit) — single source in config/links.js

const INTRO_BUBBLES = [
  "I\u2019ve looked across the choices you made \u2014 and how you explained them.",
  "Let me show you the first layer of your pattern.",
];
const OUTRO_BUBBLES = [
  "This is only the first layer.",
  "Your full report connects these patterns with your reflections, your development priorities, and what you told me about your communication.",
];
const POST_BUBBLES = [
  "Done. Your full Audit report is on its way.",
  "If you don\u2019t see it, check your Spam or Promotions folder too.",
];

// Mobile-only: brief "analyzing" beat shown automatically once the intro
// speech ends, before all 3 pattern cards appear together (replaces the
// desktop's tap-to-reveal-one-at-a-time flow — see the `isMobile` branch
// in `advance()` below).
const MOBILE_LOADING_MS = 2200;
const MOBILE_INTRO_AUTO_ADVANCE_MS = 2200;
const MOBILE_OUTRO_AUTO_ADVANCE_MS = 2000;

// Max 2 short sentences on screen — the full text lives in the payload.
const firstSentences = (text, n = 2) =>
  (text.match(/[^.!?]+[.!?]+/g) || [text]).slice(0, n).join(" ").trim();

const SECTION_DEFS = [
  { key: "naturalStrength",       kicker: "Your Natural Strength" },
  { key: "patternToWatch",        kicker: "A Pattern to Explore" },
  { key: "firstDevelopmentFocus", kicker: "Your First Development Focus" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ResultsRoom() {
  const {
    answers, tags, reflections, selectedPresenter, startedAt, completedAt,
    setPatternRead, setCanProceed, setOverrideMessage,
    coachingSupportInterest, nextPresentationTiming,
    presentationContext, presentationContextOther,
    supportPriorities, supportPrioritiesOther, fundingSource,
  } = useAudit();
  const navigate = useNavigate();
  const isMobile = useIsMobileAudit();

  const read = useMemo(
    () => buildPatternRead({ answers, tags, reflections }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(() => { setPatternRead(read); }, [read, setPatternRead]);
  useEffect(() => { setOverrideMessage(""); setCanProceed(false); }, [setOverrideMessage, setCanProceed]);

  // { phase: "intro"|"sections"|"outro"|"form"|"post"|"cta", idx }
  const [flow, setFlow] = useState({ phase: "intro", idx: 0 });
  const [form, setForm] = useState({ firstName: "", email: "", role: "" });
  // OP17 consent/privacy section — required Audit Data Processing
  // acknowledgement + optional email marketing consent. Unchecked by
  // default; the optional one must never gate submission.
  const [dataProcessingAcknowledged, setDataProcessingAcknowledged] = useState(false);
  const [emailMarketingConsent, setEmailMarketingConsent] = useState(false);
  // Covers the WHOLE submit sequence (consent save -> pipeline enqueue),
  // not just the consent call — the button (and the retry button) stay
  // disabled for the full round trip so a double-click/second click can
  // never fire a second POST /api/audit/complete.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // Full Audit backend orchestration (Operations 2-15 -> capture_audit_report),
  // reached via POST /api/audit/complete + polling GET /api/audit/complete/{id}.
  // status: "idle" | "queued" | "processing" | "completed" | "failed"
  const [pipeline, setPipeline] = useState({ status: "idle", runId: null, auditId: null });
  const pollTimerRef = useRef(null);
  const formStartedRef = useRef(false);
  const trackFormStarted = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    capture("form_started", { form_id: "audit_report_form", page_path: window.location.pathname, form_type: "audit_contact" });
  };
  // One id per mount — stable across repeated submit clicks so a
  // double-click/network retry of the SAME attempt is de-duped
  // server-side instead of creating duplicate consent evidence.
  const [consentRequestId] = useState(
    () => (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `req_${Date.now()}_${Math.random().toString(36).slice(2)}`
  );
  // Only ever set when analytics consent exists AND GA actually provides
  // one (see getGaClientId) — stays null otherwise. Surfaced as a hidden
  // field on the form below; never shown/edited by the user.
  const [gaClientId, setGaClientId] = useState(null);
  useEffect(() => {
    getGaClientId().then(setGaClientId);
  }, []);
  // Google Ads -> Website -> Audit attribution bridge (additive): the
  // CURRENT PostHog distinct_id at submit time — an attribution/
  // analytics alias only, same status as gaClientId above, never a
  // contact/person identity. `posthog.get_distinct_id()` is
  // synchronous once the SDK has loaded (consent-gated, same as
  // everything else in lib/analytics.js).
  const [posthogDistinctId, setPosthogDistinctId] = useState(null);
  useEffect(() => {
    try {
      if (window.posthog?.get_distinct_id) setPosthogDistinctId(window.posthog.get_distinct_id());
    } catch { /* no-op */ }
  }, []);

  const advance = () => {
    setFlow((f) => {
      if (f.phase === "intro")
        return f.idx < INTRO_BUBBLES.length - 1
          ? { phase: "intro", idx: f.idx + 1 }
          // Mobile: feedback loads/reveals automatically (all 3 cards at
          // once) instead of the desktop's tap-to-reveal-one-at-a-time.
          : (isMobile ? { phase: "loading", idx: 0 } : { phase: "sections", idx: 1 });
      if (f.phase === "sections")
        return f.idx < SECTION_DEFS.length ? { phase: "sections", idx: f.idx + 1 } : { phase: "outro", idx: 0 };
      if (f.phase === "outro")
        return f.idx < OUTRO_BUBBLES.length - 1 ? { phase: "outro", idx: f.idx + 1 } : { phase: "form", idx: 0 };
      if (f.phase === "post")
        return f.idx < POST_BUBBLES.length - 1 ? { phase: "post", idx: f.idx + 1 } : { phase: "cta", idx: 0 };
      return f;
    });
  };

  // Mobile only — three auto-progress beats, none of which exist on
  // desktop (which stays fully tap-driven):
  //   1. "intro" (the 2 opening lines) advances on its own too, so the
  //      whole hand-off from Room 07 into the loading state needs zero
  //      taps — the loading message appears automatically as soon as the
  //      last intro line has had its moment, not on a user action.
  //   2. "loading" always resolves on its own after a short beat, then
  //      reveals all 3 pattern cards together (idx = SECTION_DEFS.length).
  //   3. "outro" (the "this is only the first layer…" bubbles) advances on
  //      its own every 2s SO THE USER NEVER HAS TO TAP, but a tap still
  //      works too — whichever happens first wins; the effect's own
  //      cleanup on the next `flow` change cancels the other one.
  useEffect(() => {
    if (!isMobile) return;
    if (flow.phase === "intro") {
      const t = setTimeout(() => advance(), MOBILE_INTRO_AUTO_ADVANCE_MS);
      return () => clearTimeout(t);
    }
    if (flow.phase === "loading") {
      const t = setTimeout(() => {
        setFlow({ phase: "sections", idx: SECTION_DEFS.length });
      }, MOBILE_LOADING_MS);
      return () => clearTimeout(t);
    }
    if (flow.phase === "outro") {
      const t = setTimeout(() => advance(), MOBILE_OUTRO_AUTO_ADVANCE_MS);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, flow.phase, flow.idx]);

  const bubbleText =
    flow.phase === "intro" ? INTRO_BUBBLES[flow.idx]
    : flow.phase === "outro" ? OUTRO_BUBBLES[flow.idx]
    : flow.phase === "post" ? POST_BUBBLES[flow.idx]
    : "";

  const inCatcher = ["intro", "outro", "post"].includes(flow.phase) ||
    (flow.phase === "sections" && flow.idx <= SECTION_DEFS.length);
  const visibleSections = flow.phase === "sections" ? flow.idx : 0;
  // Cards are fully removed once the outro bubbles start — form comes after.
  const showSections = flow.phase === "sections" && visibleSections > 0;

  const formValid = form.firstName.trim().length > 0 && EMAIL_RE.test(form.email.trim()) && dataProcessingAcknowledged;

  // ---------------------------------------------------------------------
  // Full Audit backend orchestration — POST /api/audit/complete + poll
  // GET /api/audit/complete/{pipelineRunId} until completed/failed.
  // ---------------------------------------------------------------------
  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };
  // Cleanup on unmount only — never on re-render.
  useEffect(() => () => stopPolling(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const pollPipelineStatus = async (runId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit/complete/${runId}`);
      if (!res.ok) return; // transient — the next tick will try again
      const data = await res.json();
      if (data.status === "completed") {
        stopPolling();
        setPipeline({ status: "completed", runId, auditId: data.auditId || null });
        setFlow({ phase: "post", idx: 0 });
        // THE identity promotion point: the backend has now RESOLVED the
        // canonical contact_id (email-only resolution, unchanged) — this
        // is the ONLY moment this visitor is ever identified in PostHog,
        // and contact_id is the ONLY value ever used as the distinct_id
        // (never email/gclid/ga_client_id/posthog_distinct_id — those
        // stay attribution/analytics aliases only). posthog-js's own
        // identify() call automatically links the browser's PRIOR
        // anonymous distinct_id (every website/audit_started event so
        // far) to this new identified person — no manual alias() needed,
        // and nothing here ever calls reset() (that would break the
        // exact link this is trying to create).
        if (data.contactId) identify(data.contactId);
        // Business event — fired only here, upon a backend-confirmed
        // "completed" status, never from a frontend click/intent alone.
        // Finalize the Performance Map room's own active time into the
        // running total BEFORE reading it, so audit_active_duration_ms
        // includes every room, including the one just completed in.
        exitRoom("audit_completed");
        const totals = getAuditTotals();
        try { sessionStorage.setItem("iag_audit_completed_v1", "1"); } catch { /* no-op */ }
        // One-time conversion guard — never fires PostHog/ChatGPT Ads
        // completion events more than once for the same pipeline run
        // (concurrent poll-tick race, remount, or a page refresh after
        // completion all hit this same "completed" branch again).
        if (claimAuditCompletedEvent(runId)) {
          const completionAttribution = getAttributionContext() || {};
          capture("audit_completed", {
            auditId: data.auditId || null, pipelineRunId: runId,
            contact_id: data.contactId || null,
            link_id: completionAttribution.link_id || null,
            audit_active_duration_ms: totals.audit_active_duration_ms,
            audit_elapsed_duration_ms: totals.audit_elapsed_duration_ms,
            rooms_visited_count: totals.rooms_visited_count,
            rooms_completed_count: totals.rooms_completed_count,
            gclid: completionAttribution.gclid || null,
            utm_source: completionAttribution.utm_source || null,
            utm_medium: completionAttribution.utm_medium || null,
            utm_campaign: completionAttribution.utm_campaign || null,
            utm_content: completionAttribution.utm_content || null,
            utm_term: completionAttribution.utm_term || null,
            landing_page: completionAttribution.landing_page || null,
          });
          pushDataLayerEvent("audit_completed", { auditId: data.auditId || null });
          captureOaiqEvent("audit_completed");
        }
      } else if (data.status === "failed") {
        stopPolling();
        // Technical detail stays in console only — never shown to the user.
        console.error("Audit pipeline failed:", data.failedOperation, data.failureDetail);
        setPipeline({ status: "failed", runId, auditId: null, finalFailure: !!data.finalFailure });
        setFlow({ phase: "pipelineFailed", idx: 0 });
      } else {
        setPipeline((p) => ({ ...p, status: data.status }));
      }
    } catch (err) {
      // One bad poll tick must never fail the whole flow — keep polling.
      console.error("Failed to poll Audit pipeline status:", err);
    }
  };

  const startPolling = (runId) => {
    stopPolling();
    pollPipelineStatus(runId);
    pollTimerRef.current = setInterval(() => pollPipelineStatus(runId), PIPELINE_POLL_INTERVAL_MS);
  };

  // Shared by the initial form submit AND the "Try again" retry action —
  // reuses the exact same consent -> completion order and the backend's
  // own idempotency (same contact/answers -> same pipelineRunId).
  const runSubmission = async (contact) => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const consentRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit/consent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contact: { firstName: contact.firstName, email: contact.email },
          dataProcessingAcknowledged,
          emailMarketingConsent,
          consentRequestId,
        }),
      });
      if (!consentRes.ok) throw new Error(`consent request failed: ${consentRes.status}`);
      // Identity promotion happens LATER, in pollPipelineStatus below,
      // once the backend actually RESOLVES the canonical contact_id —
      // never here. Email is never the PostHog distinct_id (Google Ads
      // -> Website -> Audit -> Consultation identity-stitching fix,
      // 2026-09). Consent evidence itself doesn't require identity.
      // Lightweight semantic form-funnel event — no field values, ever.
      capture("form_submitted", { form_id: "audit_report_form", page_path: window.location.pathname, form_type: "audit_contact" });
    } catch (err) {
      console.error("Failed to record Audit consent evidence:", err);
      setIsSubmitting(false);
      setSubmitError("We couldn't save your preferences. Please try again.");
      return;
    }

    try {
      const auditAnalysisInput = buildAuditAnalysisInput({ answers, reflections, tags, startedAt, completedAt });
      const attribution = getAttributionContext() || {};
      const completeRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          auditAnalysisInput,
          contact,
          reportDeliveryRequested: true,
          gaClientId,
          gclid: attribution.gclid || null,
          oppref: attribution.oppref || null,
          utmSource: attribution.utm_source || null,
          utmMedium: attribution.utm_medium || null,
          utmCampaign: attribution.utm_campaign || null,
          utmContent: attribution.utm_content || null,
          utmTerm: attribution.utm_term || null,
          utmId: attribution.utm_id || null,
          landingPage: attribution.landing_page || null,
          referrer: attribution.referrer || null,
          linkId: attribution.link_id || null,
          posthogDistinctId,
          coachingSupportInterest, nextPresentationTiming,
          presentationContext, presentationContextOther,
          supportPriorities, supportPrioritiesOther, fundingSource,
        }),
      });
      if (!completeRes.ok) throw new Error(`completion request failed: ${completeRes.status}`);
      const data = await completeRes.json();
      setPipeline({ status: "queued", runId: data.pipelineRunId, auditId: null });
      setFlow({ phase: "pipeline", idx: 0 });
      startPolling(data.pipelineRunId);
    } catch (err) {
      console.error("Failed to start Audit report generation:", err);
      setSubmitError("We couldn't start preparing your report. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!formValid || isSubmitting) return;
    runSubmission({
      firstName: form.firstName.trim(),
      email: form.email.trim(),
      role: form.role.trim() || null,
    });
  };

  const retry = async () => {
    if (isSubmitting || !pipeline.runId) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // Resilient report generation — this is a true retry of the SAME
      // immutable submission (POST .../retry, never re-posting consent
      // or /api/audit/complete): no changed answers are required, and
      // the backend's own idempotency on the original submission is
      // never bypassed. The next attempt runs in Recovery Mode
      // server-side (see audit_pipeline.request_pipeline_retry) —
      // nothing further is needed client-side for that.
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audit/complete/${pipeline.runId}/retry`, { method: "POST" });
      if (!res.ok) throw new Error(`retry request failed: ${res.status}`);
      setFlow({ phase: "pipeline", idx: 0 });
      startPolling(pipeline.runId);
    } catch (err) {
      console.error("Failed to retry Audit report generation:", err);
      setSubmitError("We couldn't retry just yet. Please try again in a moment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const presenter = selectedPresenter;
  // After the form is submitted, the presenter faces the camera with open,
  // inviting arms; before that he stays in the thinking pose.
  const pose = ["post", "cta"].includes(flow.phase) ? "welcomeArms" : "thinking";
  const presenterSrc = presenter
    ? `${presenter.avatarBase}/${presenter.poses[pose]}`
    : "";

  const catcherLabel = flow.phase === "sections" ? "tap to reveal" : "tap to continue";

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="results-room" style={{ background: "#141310" }}>
      {/* Background — dedicated results-room scene */}
      <div aria-hidden="true" className="absolute inset-0"
        style={{ backgroundImage: "url(/audit/bg_results.webp)", backgroundSize: "cover", backgroundPosition: "center" }} />

      {/* ---------- LEFT — feedback content ------------------------------- */}
      {/* zIndex above the tap-catcher (88) so the panel stays scrollable */}
      <div
        className="absolute flex flex-col"
        style={{ left: "5%", top: "10%", width: "44%", maxHeight: "74%", zIndex: 90 }}
        data-testid="results-content"
      >
        <div className="overflow-y-auto min-h-0 pr-2 space-y-4" style={{ overscrollBehavior: "contain" }}>
          {/* Pattern sections — revealed one at a time, fade out before the form */}
          <AnimatePresence>
            {showSections && SECTION_DEFS.slice(0, visibleSections).map((def, i) => {
              const finding = read[def.key];
              return (
                <motion.div
                  key={def.key}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14, transition: { duration: 0.45, delay: i * 0.08 } }}
                  transition={{ duration: 0.55, ease: EASE }}
                  className="border shadow-[0_16px_40px_rgba(0,0,0,0.35)] px-7 py-6"
                  style={{ background: "rgba(252, 248, 238, 0.96)", border: "1px solid rgba(60,45,25,0.15)" }}
                  data-testid={`results-section-${def.key}`}
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-terracotta mb-2">
                    {def.kicker}
                  </p>
                  <p className="font-serif text-xl md:text-2xl leading-snug text-ink mb-2">
                    {finding.title}
                  </p>
                  <p className="font-body text-sm md:text-[15px] leading-relaxed text-ink-soft">
                    {firstSentences(finding.explanation, 2)}
                  </p>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {/* Mobile-only "analyzing" beat — auto-resolves into all 3 cards
              together (see the `isMobile` branch in `advance()`/the
              auto-progress effect above). Desktop never enters this phase. */}
          <AnimatePresence>
            {flow.phase === "loading" && (
              <motion.div
                key="feedback-loading"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.55, ease: EASE }}
                className="shadow-[0_18px_48px_rgba(0,0,0,0.4)] px-7 py-6"
                style={{ background: "rgba(252, 248, 238, 0.97)", border: "1px solid rgba(60,45,25,0.15)" }}
                data-testid="results-feedback-loading"
              >
                <div className="flex items-center gap-3 mb-1">
                  <span
                    aria-hidden="true"
                    className="inline-block h-3 w-3 rounded-full animate-spin"
                    style={{ border: "2px solid rgba(218,152,60,0.35)", borderTopColor: "#da983c" }}
                  />
                  <p className="font-serif text-xl leading-tight text-ink">We analyze your answers…</p>
                </div>
                <p className="font-body text-sm leading-relaxed text-ink-soft">
                  Your feedback will be ready in about a minute.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Report form */}
          <AnimatePresence>
            {flow.phase === "form" && (
              <motion.form
                key="report-form"
                onSubmit={submit}
                onChangeCapture={trackFormStarted}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.55, ease: EASE }}
                className="shadow-[0_18px_48px_rgba(0,0,0,0.4)] px-7 py-6"
                style={{ background: "rgba(252, 248, 238, 0.97)", border: "1px solid rgba(60,45,25,0.15)" }}
                data-testid="report-form"
              >
                <p className="font-serif text-2xl leading-tight text-ink mb-2">
                  Get your full InterAct Performance Audit
                </p>
                <p className="font-body text-sm leading-relaxed text-ink-soft mb-5">
                  Your personalised report will include the patterns behind your choices,
                  what may be worth prioritising next, and a practical exercise to try.
                </p>
                <div className="space-y-3">
                  <input
                    type="text" required value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    placeholder="First name *"
                    data-testid="report-firstname"
                    className="w-full bg-white/80 border border-ink/20 px-4 py-3 font-body text-ink placeholder:text-ink/45 focus:outline-none focus:border-terracotta transition-colors"
                  />
                  <input
                    type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email *"
                    data-testid="report-email"
                    className="w-full bg-white/80 border border-ink/20 px-4 py-3 font-body text-ink placeholder:text-ink/45 focus:outline-none focus:border-terracotta transition-colors"
                  />
                  <input
                    type="text" value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    placeholder="Role (optional)"
                    data-testid="report-role"
                    className="w-full bg-white/80 border border-ink/20 px-4 py-3 font-body text-ink placeholder:text-ink/45 focus:outline-none focus:border-terracotta transition-colors"
                  />
                </div>

                {/* ---------- OP17 consent/privacy section --------------- */}
                {/* 1. REQUIRED — Audit Data Processing acknowledgement */}
                <label
                  className="mt-4 flex items-start gap-3 cursor-pointer select-none"
                  data-testid="consent-required-label"
                >
                  <input
                    type="checkbox"
                    required
                    checked={dataProcessingAcknowledged}
                    onChange={(e) => setDataProcessingAcknowledged(e.target.checked)}
                    data-testid="consent-data-processing-checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 accent-terracotta cursor-pointer"
                  />
                  <span className="font-body text-[13px] leading-relaxed text-ink-soft">
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-terracotta mr-1.5">
                      Required
                    </span>
                    I understand that InterAct Globally will process the information I provide to
                    generate, personalize and deliver my Performance Audit report, in accordance
                    with the{" "}
                    <a
                      href={ROUTES.privacy}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="consent-privacy-policy-link"
                      className="underline text-terracotta hover:text-ink transition-colors"
                    >
                      Privacy Policy
                    </a>
                    .
                  </span>
                </label>

                {/* 2. OPTIONAL — newsletter / email marketing consent */}
                <div
                  className="mt-4 p-4 border border-dashed border-terracotta/40"
                  style={{ background: "rgba(218,152,60,0.08)", borderRadius: 10 }}
                  data-testid="consent-optional-section"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <p className="font-serif text-[15px] text-ink">Keep improving after your Audit</p>
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-soft/70 border border-ink/20 rounded-full px-2 py-0.5">
                      Optional
                    </span>
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={emailMarketingConsent}
                      onChange={(e) => setEmailMarketingConsent(e.target.checked)}
                      data-testid="consent-email-marketing-checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-terracotta cursor-pointer"
                    />
                    <span className="font-body text-[13px] leading-relaxed text-ink">
                      Yes, I&rsquo;d like to keep improving my presentations.
                    </span>
                  </label>
                  <p className="font-body text-xs leading-relaxed text-ink-soft/80 mt-2">
                    Get the InterAct newsletter with practical speaking tips, exercises and useful
                    resources, plus occasional updates about coaching programmes and services.
                    Unsubscribe anytime.
                  </p>
                </div>

                {/* Hidden — only ever populated when analytics consent exists
                    AND GA actually provides a client_id (see getGaClientId in
                    lib/analytics.js). Not user-visible/editable; carried
                    through to the OP16/n8n CRM payload as an attribution
                    alias only, never a public/query-string value. */}
                <input type="hidden" name="ga_client_id" value={gaClientId || ""} data-testid="report-ga-client-id" />
                {submitError && (
                  <p className="mt-3 font-body text-xs leading-relaxed text-red-700" data-testid="report-submit-error">
                    {submitError}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={!formValid || isSubmitting}
                  data-testid="report-submit"
                  className="mt-5 w-full font-mono text-[11px] uppercase tracking-[0.22em] text-ink py-3.5 transition-all hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: "#da983c", minHeight: 46 }}
                >
                  {isSubmitting ? "Sending\u2026" : "Send my full report"}
                </button>
              </motion.form>
            )}
          </AnimatePresence>

          {/* Pipeline loading — Operations 2-15 running server-side.
              Minimal by design: one friendly state, no progress %, no
              fake step animations. */}
          <AnimatePresence>
            {flow.phase === "pipeline" && (
              <motion.div
                key="pipeline-loading"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.55, ease: EASE }}
                className="shadow-[0_18px_48px_rgba(0,0,0,0.4)] px-7 py-6"
                style={{ background: "rgba(252, 248, 238, 0.97)", border: "1px solid rgba(60,45,25,0.15)" }}
                data-testid="results-pipeline-loading"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span
                    aria-hidden="true"
                    className="inline-block h-2.5 w-2.5 rounded-full animate-pulse"
                    style={{ background: "#da983c" }}
                  />
                  <p className="font-serif text-2xl leading-tight text-ink">Analyzing your results&hellip;</p>
                </div>
                <p className="font-body text-sm leading-relaxed text-ink-soft">
                  We&rsquo;re preparing your personalized Performance Audit. This may take a moment.
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pipeline failed — generic, non-technical message + one retry. */}
          <AnimatePresence>
            {flow.phase === "pipelineFailed" && (
              <motion.div
                key="pipeline-failed"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.55, ease: EASE }}
                className="shadow-[0_18px_48px_rgba(0,0,0,0.4)] px-7 py-6"
                style={{ background: "rgba(252, 248, 238, 0.97)", border: "1px solid rgba(60,45,25,0.15)" }}
                data-testid="results-pipeline-failed"
              >
                <p className="font-serif text-2xl leading-tight text-ink mb-2">
                  {pipeline.finalFailure
                    ? "We&rsquo;re still working on your Audit report."
                    : "We couldn&rsquo;t finish preparing your Audit."}
                </p>
                <p className="font-body text-sm leading-relaxed text-ink-soft mb-4">
                  {pipeline.finalFailure
                    ? "Your details are saved \u2014 our team will follow up by email shortly. You're welcome to try again in the meantime."
                    : "Your details are saved. Please try again."}
                </p>
                {submitError && (
                  <p className="font-body text-xs leading-relaxed text-red-700 mb-3" data-testid="pipeline-retry-error">
                    {submitError}
                  </p>
                )}
                <button
                  type="button"
                  onClick={retry}
                  disabled={isSubmitting}
                  data-testid="results-pipeline-retry"
                  className="w-full font-mono text-[11px] uppercase tracking-[0.22em] text-ink py-3.5 transition-all hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: "#da983c", minHeight: 46 }}
                >
                  {isSubmitting ? "Trying again\u2026" : "Try again"}
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Booking CTA — after the post-submit bubbles */}
          <AnimatePresence>
            {flow.phase === "cta" && (
              <motion.div
                key="booking-cta"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="shadow-[0_18px_48px_rgba(0,0,0,0.4)] px-8 py-7"
                style={{ background: "rgba(252, 248, 238, 0.97)", border: "1px solid rgba(60,45,25,0.15)" }}
                data-testid="results-cta"
                data-audit-id={pipeline.auditId || ""}
              >
                <p className="font-serif text-2xl md:text-3xl leading-tight text-ink mb-2">
                  Preparing something important?
                </p>
                <p className="font-body text-sm md:text-base leading-relaxed text-ink-soft mb-6">
                  Work through your results and your real presentation with Christos.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    trackCta({ ctaId: "results-book-cta", location: "audit_results", href: BOOKING_LINK });
                    navigate(BOOKING_LINK);
                  }}
                  data-testid="results-book-cta"
                  className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink px-8 py-3.5 transition-all hover:brightness-110 shadow-lg"
                  style={{ background: "#da983c", minHeight: 46 }}
                >
                  Book a Free Consultation with Christos
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Mobile-only sticky CTA, centered under the feedback cards while
          they're on screen — gives users a clear, explicit way forward
          into the report instead of relying only on the invisible
          tap-anywhere catcher. Desktop keeps its tap-to-reveal flow with
          no equivalent button. */}
      {isMobile && flow.phase === "sections" && (
        <motion.button
          type="button"
          onClick={advance}
          data-testid="results-send-report-cta"
          animate={{
            scale: [1, 1.045, 1],
            boxShadow: [
              "0 10px 28px rgba(0,0,0,0.4), 0 0 0px rgba(218,152,60,0)",
              "0 10px 28px rgba(0,0,0,0.4), 0 0 22px rgba(218,152,60,0.65)",
              "0 10px 28px rgba(0,0,0,0.4), 0 0 0px rgba(218,152,60,0)",
            ],
          }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute left-1/2 -translate-x-1/2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink px-7 transition-transform hover:-translate-y-0.5"
          style={{ bottom: 64, background: "#da983c", minHeight: 46, borderRadius: 999, zIndex: 95 }}
        >
          Send me my full report
        </motion.button>
      )}

      {/* ---------- Presenter (RIGHT, thinking) ---------------------------- */}
      {presenter && (
        <motion.div
          className="absolute"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, ease: EASE }}
          style={{
            left: "82%",
            bottom: "-6%",
            height: `${BASE_PRESENTER_HEIGHT}%`,
            zIndex: 30,
            transform: "translateX(-50%)",
            aspectRatio: "1122 / 1402",
            pointerEvents: "none",
          }}
          data-testid="results-presenter"
        >
          <PresenterFloorShadow />
          <img
            src={presenterSrc}
            alt={presenter.name}
            draggable={false}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="select-none block"
            style={{
              height: "100%", width: "100%", objectFit: "contain",
              filter: "brightness(0.95) sepia(0.1)",
              WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)",
              maskImage:       "linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)",
            }}
            data-testid={`results-presenter-img-${pose}`}
          />
        </motion.div>
      )}

      {/* ---------- Speech bubble (close to the presenter) ----------------- */}
      <AnimatePresence initial={false}>
        {bubbleText && (
          <motion.div
            key={bubbleText}
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="absolute"
            style={{ top: "6%", right: "17%", zIndex: 100, maxWidth: 400 }}
            data-testid="results-bubble"
          >
            <div className="relative bg-white text-[#1B1B18] px-6 py-4 shadow-[0_16px_40px_rgba(0,0,0,0.4)]" style={{ borderRadius: 26 }}>
              <p className="font-serif text-lg leading-snug">{bubbleText}</p>
              <span aria-hidden="true" className="absolute"
                style={{ right: 24, bottom: -16, width: 0, height: 0,
                  borderLeft: "16px solid transparent", borderRight: "10px solid transparent",
                  borderTop: "20px solid #ffffff", filter: "drop-shadow(0 4px 4px rgba(0,0,0,0.15))" }} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---------- Corner brand mark -------------------------------------- */}
      <div className="absolute right-6 top-5 flex flex-col items-end pointer-events-none" style={{ zIndex: 50 }}>
        <p className="font-serif italic text-paper text-lg md:text-2xl leading-none tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]">
          InterAct Globally
        </p>
        <p className="font-hand text-mustard text-base md:text-xl leading-tight mt-1 drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]">
          your performance map
        </p>
      </div>

      {/* ---------- Tap catcher --------------------------------------------- */}
      {inCatcher && (
        <button
          type="button"
          onClick={advance}
          data-testid="results-advance"
          aria-label="Continue"
          className="absolute inset-0 cursor-pointer bg-transparent"
          style={{ zIndex: 88 }}
        >
          <span aria-hidden="true"
            className="absolute bottom-24 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.28em] text-paper/80 animate-pulse drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]">
            {catcherLabel}
          </span>
        </button>
      )}
    </div>
  );
}

export default ResultsRoom;
